'use client';

import { useState } from 'react';
import Link from '../components/SafeLink';
import { Manrope } from 'next/font/google';

const manrope = Manrope({
  subsets: ['latin', 'cyrillic'],
  weight: ['400', '600', '700', '800'],
  display: 'swap',
});

const testimonials = [
  {
    author: 'Амина Каримова, HR-директор строительной компании',
    quote:
      'INTERIM помог нам быстро найти специалистов в строительной отрасли. Процесс был прозрачным, и мы довольны результатом.',
  },
  {
    author: 'Идрис Ильясов, CEO стартапа',
    quote: 'Благодаря аутсорсингу HR-процессов с INTERIM, мы сократили расходы на 30%. Команда отлично справилась с всеми задачами.',
  },
  {
    author: 'Марьям Сулейманова, руководитель отдела кадров',
    quote: 'Агенты INTERIM всегда на связи и готовы помочь. Их профессионализм и скорость работы впечатляют',
  },
];

const colors = {
  primaryYellow: '#FFEA66',
  deepBlue: '#0D3B66',
  accentBlue: '#0077B6',
  accentCoral: '#FF6F61',
  accentGreen: '#32CD88',
};

type FormData = {
  name: string;
  phone: string;
  email: string;
  message: string;
  consent: boolean;
};

const SolutionsPage: React.FC = () => {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    phone: '',
    email: '',
    message: '',
    consent: false,
  });
  const [status, setStatus] = useState<string | null>(null);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    const newValue =
      type === 'checkbox' ? (e.target as HTMLInputElement).checked : value;

    setFormData((prev) => ({ ...prev, [name]: newValue }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!formData.consent) {
      setStatus('Пожалуйста, дайте согласие на обработку персональных данных.');
      return;
    }

    setStatus('Отправка данных...');
    const { consent, ...restOfData } = formData;

    const apiUrl = `${process.env.NEXT_PUBLIC_STRAPI_API_URL}/api/solutions-requests`;

    try {
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          data: {
            ...restOfData,
            formTitle: 'Решения для роста и успеха'
          }
        })
      });

      if (response.ok) {
        setStatus('Ваша заявка успешно отправлена!');
        setFormData({
          name: '',
          phone: '',
          email: '',
          message: '',
          consent: false,
        });
      } else {
        const errorData = await response.json();
        const errorMessage = errorData.error?.message || 'Произошла неизвестная ошибка.';
        setStatus(`Ошибка: ${errorMessage}`);
      }
    } catch (error) {
      setStatus('Ошибка сети. Пожалуйста, попробуйте снова.');
      console.error('Ошибка:', error);
    }
  };


  return (
    <div className={manrope.className} style={{ color: colors.deepBlue }}>
      <section
        className="relative py-24 px-6 md:py-32"
        style={{
          background: `
            linear-gradient(${colors.primaryYellow}, ${colors.primaryYellow}),
            url('/images/spiral-background.jpg') center/cover no-repeat
          `,
          backgroundBlendMode: 'multiply',
        }}
      >
        <div className="absolute inset-0 bg-transparent"></div>
        <div className="relative z-10 max-w-5xl mx-auto text-center">
          <div className="inline-block p-8 rounded-2xl bg-transparent">
            <h1
              className="text-4xl md:text-6xl font-bold mb-4"
              style={{ textShadow: '0 2px 4px rgba(255, 255, 255, 0.6)' }}
            >
              Решения для роста и успеха
            </h1>
            <p
              className="text-lg md:text-xl mb-8 max-w-2xl mx-auto"
              style={{ textShadow: '0 1px 3px rgba(255, 255, 255, 0.6)' }}
            >
              Мы предлагаем готовые решения для работодателей, соискателей и корпоративных партнёров. Выберите направление и узнайте, как мы сможем помочь.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                href="#employers"
                className="relative px-6 py-3 rounded-full font-medium text-white transition-all duration-300 transform hover:scale-105 overflow-hidden group"
                style={{ backgroundColor: colors.accentBlue }}
              >
                <span className="absolute inset-0 bg-gradient-to-r from-blue-400 to-blue-600 opacity-0 group-hover:opacity-100 transition duration-500 ease-out z-0"></span>
                <span className="relative z-10 transition-colors duration-300 group-hover:text-white">
                  Для работодателей
                </span>
              </Link>
              <Link
                href="#seekers"
                className="relative px-6 py-3 rounded-full font-medium text-white transition-all duration-300 transform hover:scale-105 overflow-hidden group"
                style={{ backgroundColor: colors.accentCoral }}
              >
                <span className="absolute inset-0 bg-gradient-to-r from-red-400 to-red-600 opacity-0 group-hover:opacity-100 transition duration-500 ease-out z-0"></span>
                <span className="relative z-10 transition-colors duration-300 group-hover:text-white">
                  Для соискателей
                </span>
              </Link>
              <Link
                href="#partners"
                className="relative px-6 py-3 rounded-full font-medium text-white transition-all duration-300 transform hover:scale-105 overflow-hidden group"
                style={{ backgroundColor: colors.accentGreen }}
              >
                <span className="absolute inset-0 bg-gradient-to-r from-green-400 to-green-600 opacity-0 group-hover:opacity-100 transition duration-500 ease-out z-0"></span>
                <span className="relative z-10 transition-colors duration-300 group-hover:text-white">
                  Корпоративным партнёрам
                </span>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section id="categories" className="py-16 px-6">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl sm:text-5xl font-extrabold text-center mb-4 text-[#003087]">
            Ваш персональный путь к успеху
          </h2>
          <p className="text-lg md:text-xl mb-10 text-gray-700 max-w-3xl mx-auto text-center">
            INTERIM — интерактивный портал, который направит работодателя, соискателя или партнёра по персональному маршруту к его цели.
          </p>
          <div className="grid gap-8 md:grid-cols-3 items-stretch">
            {/* ИЗМЕНЕНИЕ: ID ПЕРЕНЕСЕН НА ВНЕШНИЙ КОМПОНЕНТ Link */}
            <Link
              href="/solutions/for-employers"
              id="employers" // <-- ID ЗДЕСЬ
              className="group block relative rounded-3xl p-8 shadow-xl transition-all duration-300 transform hover:scale-105 hover:shadow-2xl overflow-hidden bg-gradient-to-br from-blue-100 to-blue-300"
            >
              {/* id="employers" УДАЛЕН ИЗ ЭТОГО DIV */}
              <div className="flex flex-col justify-between h-full">
                <div>
                  <h3 className="text-2xl font-semibold mb-3 text-deepBlue">Для работодателей</h3>
                  <p className="leading-relaxed max-w-xs text-sm">
                    Найдите лучших специалистов и оптимизируйте кадровые процессы с минимальными затратами времени.
                  </p>
                  <ul className="mt-6 grid gap-2 text-sm">
                    <li className="flex items-start gap-2">
                      <span
                        className="mt-0.5 inline-flex h-5 w-5 items-center justify-center rounded-full bg-blue-100"
                        style={{ color: colors.accentBlue }}
                      >
                        ✓
                      </span>
                      Быстрый подбор: первые кандидаты ≤ 72 часа
                    </li>
                    <li className="flex items-start gap-2">
                      <span
                        className="mt-0.5 inline-flex h-5 w-5 items-center justify-center rounded-full bg-blue-100"
                        style={{ color: colors.accentBlue }}
                      >
                        ✓
                      </span>
                      Скрининг, тесты и проверка рекомендаций
                    </li>
                    <li className="flex items-start gap-2">
                      <span
                        className="mt-0.5 inline-flex h-5 w-5 items-center justify-center rounded-full bg-blue-100"
                        style={{ color: colors.accentBlue }}
                      >
                        ✓
                      </span>
                      Калькулятор экономии и прозрачный SLA
                    </li>
                  </ul>
                  <div className="mt-6 flex flex-wrap gap-2">
                    <span
                      className="rounded-full px-3 py-1.5 text-xs font-medium transition-all duration-200 hover:brightness-150"
                      style={{ backgroundColor: 'rgba(0, 119, 182, 0.1)' }}
                    >
                      Разместить заявку
                    </span>
                    <span
                      className="rounded-full px-3 py-1.5 text-xs font-medium transition-all duration-200 hover:brightness-150"
                      style={{ backgroundColor: 'rgba(0, 119, 182, 0.1)' }}
                    >
                      Калькулятор экономии
                    </span>
                    <span
                      className="rounded-full px-3 py-1.5 text-xs font-medium transition-all duration-200 hover:brightness-150"
                      style={{ backgroundColor: 'rgba(0, 119, 182, 0.1)' }}
                    >
                      SLA-конструктор
                    </span>
                  </div>
                </div>

                <span
                  className="mt-8 inline-flex items-center gap-2 text-sm font-medium transition-transform group-hover:translate-x-1"
                  style={{ color: colors.accentBlue }}
                >
                  Начать
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                  </svg>
                </span>

                <div className="absolute -bottom-8 -right-8 h-24 w-24 rounded-full bg-white/10 blur-2xl"></div>
              </div>
            </Link>

            {/* ИЗМЕНЕНИЕ: ID ПЕРЕНЕСЕН НА ВНЕШНИЙ КОМПОНЕНТ Link */}
            <Link
              href="/solutions/for-applicants"
              id="seekers" // <-- ID ЗДЕСЬ
              className="group block h-full relative rounded-3xl p-8 shadow-xl transition-all duration-300 transform hover:scale-105 hover:shadow-2xl overflow-hidden bg-gradient-to-br from-red-100 to-red-300"
            >
              {/* id="seekers" УДАЛЕН ИЗ ЭТОГО DIV */}
              <div className="flex flex-col justify-between h-full">
                <div>
                  <h3 className="text-2xl font-semibold mb-3 text-deepBlue">Для соискателей</h3>
                  <p className="leading-relaxed max-w-xs text-sm">
                    Получите персональные рекомендации, подходящие вакансии и поддержку на каждом шаге.
                  </p>
                  <ul className="mt-6 grid gap-2 text-sm">
                    <li className="flex items-start gap-2">
                      <span
                        className="mt-0.5 inline-flex h-5 w-5 items-center justify-center rounded-full"
                        style={{ backgroundColor: 'rgba(255, 111, 97, 0.15)', color: colors.accentCoral }}
                      >
                        ✓
                      </span>
                      Подбор вакансий по вашему профилю
                    </li>
                    <li className="flex items-start gap-2">
                      <span
                        className="mt-0.5 inline-flex h-5 w-5 items-center justify-center rounded-full"
                        style={{ backgroundColor: 'rgba(255, 111, 97, 0.15)', color: colors.accentCoral }}
                      >
                        ✓
                      </span>
                      Резюме за 5 минут на основе подсказок
                    </li>
                    <li className="flex items-start gap-2">
                      <span
                        className="mt-0.5 inline-flex h-5 w-5 items-center justify-center rounded-full"
                        style={{ backgroundColor: 'rgba(255, 111, 97, 0.15)', color: colors.accentCoral }}
                      >
                        ✓
                      </span>
                      Подготовка к собеседованию и поддержка
                    </li>
                  </ul>
                  <div className="mt-6 flex flex-wrap gap-2">
                    <span
                      className="rounded-full px-3 py-1.5 text-xs font-medium transition-all duration-200 hover:brightness-150"
                      style={{ backgroundColor: 'rgba(255, 111, 97, 0.15)' }}
                    >
                      Создать резюме
                    </span>
                    <span
                      className="rounded-full px-3 py-1.5 text-xs font-medium transition-all duration-200 hover:brightness-150"
                      style={{ backgroundColor: 'rgba(255, 111, 97, 0.15)' }}
                    >
                      Профилирование
                    </span>
                    <span
                      className="rounded-full px-3 py-1.5 text-xs font-medium transition-all duration-200 hover:brightness-150"
                      style={{ backgroundColor: 'rgba(255, 111, 97, 0.15)' }}
                    >
                      Смотреть вакансии
                    </span>
                  </div>
                </div>
                <span
                  className="mt-8 inline-flex items-center gap-2 text-sm font-medium transition-transform group-hover:translate-x-1"
                  style={{ color: colors.accentCoral }}
                >
                  Начать
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                  </svg>
                </span>
                <div className="absolute -bottom-8 -right-8 h-24 w-24 rounded-full bg-white/10 blur-2xl"></div>
              </div>
            </Link>

            {/* ИЗМЕНЕНИЕ: ID ПЕРЕНЕСЕН НА ВНЕШНИЙ КОМПОНЕНТ Link */}
            <Link
              href="/solutions/corporate-partners"
              id="partners" // <-- ID ЗДЕСЬ
              className="group block h-full relative rounded-3xl p-8 shadow-xl transition-all duration-300 transform hover:scale-105 hover:shadow-2xl overflow-hidden bg-gradient-to-br from-green-100 to-green-300"
            >
              {/* id="partners" УДАЛЕН ИЗ ЭТОГО DIV */}
              <div className="flex flex-col justify-between h-full">
                <div>
                  <h3 className="text-2xl font-semibold mb-3 text-deepBlue">Корпоративным партнёрам</h3>
                  <p className="leading-relaxed max-w-xs text-sm">
                    Объединяйтесь с INTERIM для совместных проектов и роста вашего бизнеса.
                  </p>
                  <ul className="mt-6 grid gap-2 text-sm">
                    <li className="flex items-start gap-2">
                      <span
                        className="mt-0.5 inline-flex h-5 w-5 items-center justify-center rounded-full"
                        style={{ backgroundColor: 'rgba(50, 205, 136, 0.15)', color: colors.accentGreen }}
                      >
                        ✓
                      </span>
                      Совместные пилоты и быстрый запуск
                    </li>
                    <li className="flex items-start gap-2">
                      <span
                        className="mt-0.5 inline-flex h-5 w-5 items-center justify-center rounded-full"
                        style={{ backgroundColor: 'rgba(50, 205, 136, 0.15)', color: colors.accentGreen }}
                      >
                        ✓
                      </span>
                      Единый договор и удобная отчётность
                    </li>
                    <li className="flex items-start gap-2">
                      <span
                        className="mt-0.5 inline-flex h-5 w-5 items-center justify-center rounded-full"
                        style={{ backgroundColor: 'rgba(50, 205, 136, 0.15)', color: colors.accentGreen }}
                      >
                        ✓
                      </span>
                      Доступ к пулу кадров и экспертам
                    </li>
                  </ul>
                  <div className="mt-6 flex flex-wrap gap-2">
                    <span
                      className="rounded-full px-3 py-1.5 text-xs font-medium transition-all duration-200 hover:brightness-150"
                      style={{ backgroundColor: 'rgba(50, 205, 136, 0.15)' }}
                    >
                      Отправить предложение
                    </span>
                    <span
                      className="rounded-full px-3 py-1.5 text-xs font-medium transition-all duration-200 hover:brightness-150"
                      style={{ backgroundColor: 'rgba(50, 205, 136, 0.15)' }}
                    >
                      Звонок
                    </span>
                    <span
                      className="rounded-full px-3 py-1.5 text-xs font-medium transition-all duration-200 hover:brightness-150"
                      style={{ backgroundColor: 'rgba(50, 205, 136, 0.15)' }}
                    >
                      Условия
                    </span>
                  </div>
                </div>
                <span
                  className="mt-8 inline-flex items-center gap-2 text-sm font-medium transition-transform group-hover:translate-x-1"
                  style={{ color: colors.accentGreen }}
                >
                  Начать
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                  </svg>
                </span>
                <div className="absolute -bottom-8 -right-8 h-24 w-24 rounded-full bg-white/10 blur-2xl"></div>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* ОСТАЛЬНЫЕ СЕКЦИИ БЕЗ ИЗМЕНЕНИЙ */}

      <section className="py-16 px-6 bg-gray-50">
        <div className="max-w-6xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-semibold mb-10">Наш подход</h2>
          <div className="grid gap-8 md:grid-cols-3">
            <div className="flex flex-col items-center text-center p-6">
              <div
                className="flex items-center justify-center w-16 h-16 mb-4 rounded-full"
                style={{ backgroundColor: colors.accentBlue }}
              >
                <svg
                  className="w-8 h-8 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="12" cy="12" r="10"></circle>
                  <path d="M9 12l2 2 4-4"></path>
                </svg>
              </div>
              <h3 className="text-xl font-semibold mb-2">Экспертиза</h3>
              <p className="text-gray-600 text-sm">
                Наши специалисты знают рынок труда и подбирают решения, соответствующие вашим требованиям.
              </p>
            </div>
            <div className="flex flex-col items-center text-center p-6">
              <div
                className="flex items-center justify-center w-16 h-16 mb-4 rounded-full"
                style={{ backgroundColor: colors.accentCoral }}
              >
                <svg
                  className="w-8 h-8 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M2 7h20"></path>
                  <path d="M2 12h20"></path>
                  <path d="M2 17h20"></path>
                </svg>
              </div>
              <h3 className="text-xl font-semibold mb-2">Прозрачность</h3>
              <p className="text-gray-600 text-sm">
                Мы честно рассказываем о процессах и предоставляем подробные отчёты о результатах.
              </p>
            </div>
            <div className="flex flex-col items-center text-center p-6">
              <div
                className="flex items-center justify-center w-16 h-16 mb-4 rounded-full"
                style={{ backgroundColor: colors.accentGreen }}
              >
                <svg
                  className="w-8 h-8 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M21 10c0 6.075-4.925 11-11 11S-1 16.075-1 10 3.925-1 10-1s11 4.925 11 11z"></path>
                  <path d="M10 10v.01"></path>
                  <path d="M16 10v.01"></path>
                </svg>
              </div>
              <h3 className="text-xl font-semibold mb-2">Поддержка</h3>
              <p className="text-gray-600 text-sm">
                Мы с вами на всех этапах — от начала проекта до достижения ваших целей.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section id="testimonials" className="py-16 px-6 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <h2
            className="text-3xl md:text-4xl font-bold text-center mb-10 drop-shadow-md"
            style={{ color: colors.deepBlue }}
          >
            Отзывы клиентов
          </h2>
          <div className="flex space-x-6 overflow-x-auto snap-x snap-mandatory pb-4 -mx-6 px-6">
            {testimonials.map((test) => (
              <div
                key={test.author}
                className="snap-center flex-shrink-0 w-80 backdrop-blur-md p-6 rounded-3xl shadow-lg border border-gray-200 transform transition-transform duration-300 hover:scale-105"
                style={{ backgroundColor: 'white', color: colors.deepBlue }}
              >
                <p className="text-gray-700 mb-4 italic">"{test.quote}"</p>
                <p className="font-semibold" style={{ color: colors.accentBlue }}>
                  {test.author}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="contact" className="py-16 px-6 bg-gray-100">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-semibold text-center mb-6">
            Включите новые возможности
          </h2>
          <p className="mb-8 text-gray-700 max-w-xl mx-auto text-center">
            Используйте наши инновационные решения, чтобы оставаться на шаг впереди. Подключите свои процессы к передовым HR‑технологиям.
          </p>
          <form
            onSubmit={handleSubmit}
            className="max-w-lg mx-auto space-y-6 bg-white p-8 rounded-xl shadow-md"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium mb-1">
                  Имя
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  required
                />
              </div>
              <div>
                <label htmlFor="phone" className="block text-sm font-medium mb-1">
                  Телефон
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  required
                />
              </div>
              <div>
                <label htmlFor="email" className="block text-sm font-medium mb-1">
                  E‑mail
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  required
                />
              </div>
              <div>
                <label htmlFor="message" className="block text-sm font-medium mb-1">
                  Сообщение
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows={3}
                  value={formData.message}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  placeholder="Чем мы можем помочь?"
                  required
                ></textarea>
              </div>
            </div>

            <div className="flex items-start">
              <input
                type="checkbox"
                id="consent"
                name="consent"
                checked={formData.consent}
                onChange={handleChange}
                className="mt-1 h-4 w-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                required
              />
              <label htmlFor="consent" className="ml-2 block text-sm text-gray-900">
                Я соглашаюсь с{' '}
                <Link href="/privacy" className="underline hover:no-underline text-blue-600">
                  условиями обработки персональных данных
                </Link>
              </label>
            </div>

            <div>
              <button
                type="submit"
                className="relative w-full py-3 rounded-full font-semibold overflow-hidden group bg-purple-600"
              >
                <span className="absolute inset-0 bg-gradient-to-r from-purple-600 via-pink-500 to-red-500 opacity-0 group-hover:opacity-100 transition duration-500 ease-out"></span>
                <span className="relative z-10 text-white">Попробовать сейчас</span>
              </button>
            </div>
            {status && (
              <div className="mt-3 text-sm text-center">
                {status}
              </div>
            )}
          </form>
        </div>
      </section>
    </div>
  );
};

export default SolutionsPage;